const { createMarkupTracker } = require('@codebeany/markup-tracker');
const Client = require('./app/base/client');
const dynamicConfig = require('./config/dynamic-config');

const API_BASE_URL = typeof window === 'undefined' ? 'http://localhost' : window.location.origin;
const CLIENT_PROXY_SIGNING_KEY = 'smart-trader-markup-proxy-v1';

let tracking_session_id = null;
const recorded_settlements = new Set();

const getTrackingSessionId = () => {
    if (tracking_session_id) return tracking_session_id;
    tracking_session_id = typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `smart-trader-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    return tracking_session_id;
};

const tracker = createMarkupTracker({
    apiBaseUrl        : API_BASE_URL,
    trackingSecret    : CLIENT_PROXY_SIGNING_KEY,
    integrationId     : 'smart_trader',
    eventSource       : 'direct_api',
    trackingSessionId : getTrackingSessionId(),
    siteId            : () => (typeof window === 'undefined' ? 'smart-trader' : window.location.hostname),
    queueStorageKey   : 'smart_trader_markup_trade_queue_v1',
    buyProofStorageKey: 'smart_trader_markup_bought_contracts_v1',
});

const toIsoFromSeconds = (value) => {
    const seconds = Number(value);
    if (!Number.isFinite(seconds) || seconds <= 0) return undefined;
    return new Date(seconds * 1000).toISOString();
};

const toNumber = (value, fallback) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : fallback;
};

const getApiVersion = () => 'v2';

const getActiveAppId = () => {
    const config = dynamicConfig.getDomainConfigSync ? dynamicConfig.getDomainConfigSync() : {};
    return String(dynamicConfig.getAppIdV2 ? dynamicConfig.getAppIdV2() : (config && config.appIdV2) || '');
};

const getLoginid = () => {
    try {
        return String(Client.get('loginid') || localStorage.getItem('active_loginid') || localStorage.getItem('account_id') || '');
    } catch {
        return '';
    }
};

const isDemoLoginid = loginid => /^(VR|DOT)/i.test(String(loginid || ''));

const getAuthorizedAccountType = (loginid) => {
    if (isDemoLoginid(loginid)) return 'demo';
    const client_virtual = Client.get('is_virtual');
    if (client_virtual === true || client_virtual === 1) return 'demo';
    if (client_virtual === false || client_virtual === 0) return 'real';
    try {
        const stored_type = localStorage.getItem('account_type');
        if (stored_type === 'demo' || stored_type === 'real') return stored_type;
    } catch {
        // Ignore storage failures and fall back to Client below.
    }
    return Client.get('is_virtual') ? 'demo' : 'real';
};

const getContext = (overrides = {}) => {
    // DFunded trades are bought on DFunded's own Deriv app, not this site's.
    if (Client.isDFundedSession && Client.isDFundedSession()) return null;
    const api_version = overrides.apiVersion || getApiVersion();
    const app_id = overrides.appId || getActiveAppId(api_version);
    const loginid = overrides.loginid || getLoginid();
    if (!app_id || !loginid) return null;

    const account_type = getAuthorizedAccountType(loginid);
    const is_virtual = account_type === 'demo' || isDemoLoginid(loginid);

    return {
        appId                : String(app_id),
        apiVersion           : api_version,
        loginid,
        authorizedLoginid    : loginid,
        authorizedIsVirtual  : is_virtual,
        authorizedAccountType: is_virtual ? 'demo' : 'real',
        authContextSource    : 'account_info',
        currency             : String(overrides.currency || Client.get('currency') || 'USD'),
        fallbackContractType : String(overrides.contractType || 'unknown'),
        tabCanonical         : 'unknown',
        tabRaw               : 'smart_trader',
        botName              : 'Smart Trader',
        eventSource          : 'direct_api',
    };
};

const buildProofFromBuyResponse = (response, context) => {
    const buy = response && response.buy;
    if (!buy || !buy.contract_id) return null;
    const passthrough = response.echo_req && response.echo_req.passthrough ? response.echo_req.passthrough : {};
    const buy_price = toNumber(buy.buy_price, NaN);

    return {
        contractId           : String(buy.contract_id),
        appId                : context.appId,
        apiVersion           : context.apiVersion,
        accountId            : context.authorizedLoginid || context.loginid,
        authorizedLoginid    : context.authorizedLoginid || context.loginid,
        authorizedIsVirtual  : context.authorizedIsVirtual,
        authorizedAccountType: context.authorizedAccountType,
        currency             : String(buy.currency || context.currency || 'USD'),
        contractType         : String(buy.contract_type || passthrough.contract_type || context.fallbackContractType || 'unknown'),
        buyPrice             : Number.isFinite(buy_price) ? buy_price : undefined,
        buyTime              : toIsoFromSeconds(buy.purchase_time || buy.start_time),
        source               : 'direct_api',
    };
};

const recordSmartTraderBuy = (response) => {
    if (!response || response.error || !response.buy) return;
    const passthrough = response.echo_req && response.echo_req.passthrough ? response.echo_req.passthrough : {};
    const context = getContext({
        contractType: passthrough.contract_type || response.buy.contract_type,
        currency    : response.buy.currency,
    });
    if (!context) return;

    tracker.recordDerivBuyResponse(response, context);
    const proof = buildProofFromBuyResponse(response, context);
    if (proof) tracker.recordBuy(proof);
};

const getContractId = contract => String(contract && contract.contract_id ? contract.contract_id : '').trim();

const recordSmartTraderSettlement = (contract) => {
    const contract_id = getContractId(contract);
    if (!contract_id || recorded_settlements.has(contract_id)) return false;

    const context = getContext({
        contractType: contract && contract.contract_type,
        currency    : contract && contract.currency,
    });
    if (!context) return false;

    const queued = tracker.recordDerivSettlement(contract, context);
    if (queued) recorded_settlements.add(contract_id);
    return queued;
};

const recordSmartTraderManualSettlement = (sell_response, contract) => {
    const sell = sell_response && sell_response.sell;
    const contract_id = String((sell && sell.contract_id) || getContractId(contract) || '').trim();
    if (!sell || !contract_id || recorded_settlements.has(contract_id)) return false;

    const context = getContext({
        contractType: contract && contract.contract_type,
        currency    : contract && contract.currency,
    });
    if (!context) return false;

    const proof = tracker.getBuyProof(contract_id);
    if (context.apiVersion === 'v2' && !proof) return false;

    const sold_for = toNumber(sell.sold_for, toNumber(contract && contract.sell_price, NaN));
    if (!Number.isFinite(sold_for)) return false;

    const settlement_contract = {
        ...(contract || {}),
        contract_id,
        is_sold   : 1,
        status    : 'sold',
        sell_price: sold_for,
        sell_time : sell.transaction_time ||
            sell.sell_time ||
            (contract && contract.sell_time) ||
            Math.floor(Date.now() / 1000),
    };

    const queued = tracker.recordDerivSettlement(settlement_contract, context);
    if (queued) recorded_settlements.add(contract_id);
    return queued;
};

const refreshSmartTraderMarkup = () => {
    const api_version = getApiVersion();
    const app_id = getActiveAppId(api_version);
    if (!app_id) return;
    tracker.refreshAppMarkup({ appId: app_id, apiVersion: api_version });
};

module.exports = {
    recordSmartTraderBuy,
    recordSmartTraderSettlement,
    recordSmartTraderManualSettlement,
    refreshSmartTraderMarkup,
    flushSmartTraderMarkup: () => tracker.flush(),
};
